import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { trackingAPI, matchingAPI, sellersAPI, postsAPI, accessAPI } from '../../services/api';
import api from '../../services/api';
import {
  Eye,
  TrendingUp,
  Target,
  ArrowUpRight,
  Flame,
  Users,
  MessageSquare,
  Building2,
  Zap,
  Sparkles,
  ArrowRight,
  PenSquare,
  Shield,
  Check,
  X,
  Loader2,
  AlertTriangle,
  Bell,
} from 'lucide-react';
import MarketIntelligence from '../../components/seller/MarketIntelligence';

export default function SellerDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    weeklyViews: 0,
    uniqueBuyers: 0,
    hotLeads: 0,
    highMatches: 0,
  });
  const [hotBuyers, setHotBuyers] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);
  const [weeklyTrend, setWeeklyTrend] = useState(0);
  const [accessRequests, setAccessRequests] = useState([]);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [processingRequest, setProcessingRequest] = useState(null);

  const sellerId = user?.sellerId;

  useEffect(() => {
    if (authLoading) return;
    if (sellerId) {
      loadDashboardData(sellerId);
    } else {
      setLoading(false);
    }
  }, [sellerId, authLoading]);

  const loadDashboardData = async (id) => {
    try {
      const [viewStatsRes, interestRes, matchesRes, messagesRes, accessRes] = await Promise.all([
        trackingAPI.getViewStats(id).catch(() => ({ data: { data: { overview: {} } } })),
        trackingAPI.getBuyerInterest(id).catch(() => ({ data: { data: { hot_leads: 0, interests: [] } } })),
        matchingAPI.getSellerMatches(id).catch(() => ({ data: { data: { recommendations: [] } } })),
        api.get('/messages/conversations').catch(() => ({ data: { data: [] } })),
        accessAPI.getIncomingRequests('pending').catch(() => ({ data: { data: { requests: [], stats: {} } } })),
      ]);

      const overview = viewStatsRes.data?.data?.overview || {};
      const interest = interestRes.data?.data || {};
      // Matching API returns { data: { recommendations: [...] } }, not { data: [...] }
      const matches = matchesRes.data?.data?.recommendations || [];
      const conversations = messagesRes.data?.data || [];

      // Calculate weekly views (use last 7 days if available, otherwise estimate from 30 days)
      const weeklyViews = overview.views_last_7_days || Math.round((overview.views_last_30_days || 0) / 4);

      // Calculate trend (compare to previous period)
      const previousWeekViews = overview.views_previous_7_days || weeklyViews;
      const trend = previousWeekViews > 0
        ? Math.round(((weeklyViews - previousWeekViews) / previousWeekViews) * 100)
        : 0;

      // Count high matches (>80%) - API returns 'score' not 'match_score'
      const highMatches = matches.filter(m => m.score >= 80).length;

      setStats({
        weeklyViews,
        uniqueBuyers: overview.unique_buyers || 0,
        hotLeads: interest.hot_leads || 0,
        highMatches,
      });

      setWeeklyTrend(trend);

      // Get top 5 hot buyers sorted by interest
      const sortedBuyers = (interest.interests || [])
        .sort((a, b) => (b.interest_score || 0) - (a.interest_score || 0))
        .slice(0, 5);
      setHotBuyers(sortedBuyers);

      // Get recent messages (last 3)
      setRecentMessages(conversations.slice(0, 3));

      // Get access requests
      const accessData = accessRes.data?.data || {};
      setAccessRequests(accessData.requests || []);
      setPendingRequestsCount(accessData.stats?.pending_count || 0);

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveAccess = async (requestId) => {
    setProcessingRequest(requestId);
    try {
      await accessAPI.approveRequest(requestId, {});
      // Remove from list
      setAccessRequests(prev => prev.filter(r => r.id !== requestId));
      setPendingRequestsCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to approve access:', error);
      alert('Failed to approve access request');
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleRejectAccess = async (requestId) => {
    setProcessingRequest(requestId);
    try {
      await accessAPI.rejectRequest(requestId, {});
      // Remove from list
      setAccessRequests(prev => prev.filter(r => r.id !== requestId));
      setPendingRequestsCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to reject access:', error);
      alert('Failed to reject access request');
    } finally {
      setProcessingRequest(null);
    }
  };

  const getAccessLevelLabel = (level) => {
    const labels = {
      interested: 'Interested Buyer',
      deep_dive: 'Deep Dive',
      deal_terms: 'Deal Terms',
    };
    return labels[level] || level;
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!user?.sellerId) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Building2 className="w-8 h-8 text-purple-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Complete Your Profile
        </h2>
        <p className="text-gray-600 mb-6">
          Set up your company profile to start getting discovered by pharma BD teams.
        </p>
        <Link to="/seller/profile" className="inline-flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700">
          Set Up Profile
          <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Prominent Access Request Banner */}
      {pendingRequestsCount > 0 && (
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                <Bell className="w-6 h-6 text-white animate-pulse" />
              </div>
              <div className="text-white">
                <p className="font-bold text-lg">
                  {pendingRequestsCount} New Access Request{pendingRequestsCount > 1 ? 's' : ''}
                </p>
                <p className="text-purple-100 text-sm">
                  Buyers want to view your confidential information
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/seller/messages"
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg font-medium transition-colors text-sm"
              >
                View in Messages
              </Link>
              <button
                onClick={() => document.getElementById('access-requests-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-4 py-2 bg-white text-purple-600 rounded-lg font-medium hover:bg-purple-50 transition-colors text-sm"
              >
                Review Now
              </button>
            </div>
          </div>
          {/* Quick preview of requests */}
          {accessRequests.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/20">
              <div className="flex flex-wrap gap-2">
                {accessRequests.slice(0, 3).map((req) => (
                  <span
                    key={req.id}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur rounded-lg text-white text-sm"
                  >
                    <span className="w-6 h-6 bg-white/30 rounded-full flex items-center justify-center text-xs font-bold">
                      {req.buyer_company_name?.charAt(0) || '?'}
                    </span>
                    {req.buyer_company_name} wants {getAccessLevelLabel(req.access_level)}
                  </span>
                ))}
                {accessRequests.length > 3 && (
                  <span className="px-3 py-1.5 text-white/80 text-sm">
                    +{accessRequests.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Your buyer interest at a glance</p>
        </div>
        <Link
          to="/seller/profile"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
        >
          <PenSquare className="w-4 h-4" />
          Edit Profile
        </Link>
      </div>

      {/* Key Metrics - 4 Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          icon={Eye}
          label="Weekly Views"
          value={stats.weeklyViews}
          trend={weeklyTrend}
          color="blue"
        />
        <MetricCard
          icon={Users}
          label="Unique Buyers"
          value={stats.uniqueBuyers}
          subtext="Total visitors"
          color="green"
        />
        <MetricCard
          icon={Flame}
          label="Hot Leads"
          value={stats.hotLeads}
          subtext="High interest"
          color="orange"
          link="/seller/interest"
          highlight={stats.hotLeads > 0}
        />
        <MetricCard
          icon={Target}
          label="Match >80%"
          value={stats.highMatches}
          subtext="Strong matches"
          color="purple"
          link="/seller/matching"
        />
      </div>

      {/* Main Content - Two Columns */}
      <div className="grid lg:grid-cols-5 gap-6">
        {/* Left/Center Column - Market Intelligence & Quick Actions */}
        <div className="lg:col-span-3 space-y-6">
          {/* Market Intelligence - Primary Focus */}
          <MarketIntelligence sellerId={sellerId} />

          {/* Intelligence Hub */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-semibold text-gray-900">Intelligence Hub</h2>
                <p className="text-xs text-gray-500">Your analytics across all modules</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Link
                to="/seller/matching"
                className="flex flex-col gap-2 p-4 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Smart Matching</p>
                    <p className="text-xs text-gray-500">Buyer intelligence</p>
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-lg font-bold text-purple-600">{stats.highMatches}</p>
                  <p className="text-xs text-gray-500">Strong matches (80%+)</p>
                </div>
              </Link>
              <Link
                to="/seller/profile#deal-comparables"
                className="flex flex-col gap-2 p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Deal Comparables</p>
                    <p className="text-xs text-gray-500">Asset-level pricing</p>
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-600">View historical deals matching your assets</p>
                  <p className="text-xs text-blue-600 mt-1">In Company Profile →</p>
                </div>
              </Link>
              <Link
                to="/seller/deal-intelligence"
                className="flex flex-col gap-2 p-4 rounded-lg border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Target className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Deal Intelligence</p>
                    <p className="text-xs text-gray-500">Full valuation analysis</p>
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-600">Valuation, insights, acquirers</p>
                  <p className="text-xs text-emerald-600 mt-1">View full analysis →</p>
                </div>
              </Link>
            </div>
          </div>

          {/* Messages */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-500" />
                <h2 className="font-semibold text-gray-900">Messages</h2>
              </div>
              <Link
                to="/seller/messages"
                className="text-sm text-purple-600 hover:text-purple-700 font-medium"
              >
                View all
              </Link>
            </div>

            <div className="divide-y divide-gray-100">
              {recentMessages.length > 0 ? (
                recentMessages.map((conv, index) => (
                  <Link
                    key={conv.id || index}
                    to="/seller/messages"
                    className="block px-5 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-medium text-sm">
                        {conv.other_party_name?.charAt(0) || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {conv.other_party_name || 'Unknown'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {conv.last_message || 'No messages yet'}
                        </p>
                      </div>
                      {conv.unread_count > 0 && (
                        <span className="w-5 h-5 bg-purple-600 text-white text-xs rounded-full flex items-center justify-center">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                  </Link>
                ))
              ) : (
                <div className="px-5 py-8 text-center">
                  <p className="text-sm text-gray-500">No messages yet</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Sidebar - Buyer Interest & Access Requests */}
        <div className="lg:col-span-2 space-y-6">
          {/* Buyer Interest */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                <h2 className="font-semibold text-gray-900">Buyer Interest</h2>
              </div>
              <Link
                to="/seller/interest"
                className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
              >
                View all
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="divide-y divide-gray-100">
              {hotBuyers.length > 0 ? (
                hotBuyers.map((buyer, index) => (
                  <div
                    key={buyer.buyer_id || index}
                    className="px-5 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold">
                        {buyer.company_name?.charAt(0) || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{buyer.company_name || 'Unknown Buyer'}</p>
                        <p className="text-xs text-gray-500">
                          {buyer.view_count} views · {formatTimeAgo(buyer.last_viewed)}
                        </p>
                      </div>
                      <InterestBadge level={buyer.interest_level} score={buyer.interest_score} />
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-5 py-8 text-center">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Eye className="w-5 h-5 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium text-sm">No buyer activity yet</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Complete your profile to attract buyers
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Access Requests */}
          <div id="access-requests-section" className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-500" />
                <h2 className="font-semibold text-gray-900">Access Requests</h2>
                {pendingRequestsCount > 0 && (
                  <span className="w-5 h-5 bg-purple-600 text-white text-xs rounded-full flex items-center justify-center">
                    {pendingRequestsCount}
                  </span>
                )}
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {accessRequests.length > 0 ? (
                accessRequests.slice(0, 5).map((request) => (
                  <div
                    key={request.id}
                    className="px-5 py-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-medium text-sm flex-shrink-0">
                          {request.buyer_company_name?.charAt(0) || '?'}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {request.buyer_company_name || 'Unknown Buyer'}
                          </p>
                          <p className="text-xs text-gray-500">
                            Requesting: {getAccessLevelLabel(request.access_level)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleApproveAccess(request.id)}
                          disabled={processingRequest === request.id}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Approve"
                        >
                          {processingRequest === request.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleRejectAccess(request.id)}
                          disabled={processingRequest === request.id}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Reject"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {request.message && (
                      <p className="text-xs text-gray-500 mt-2 pl-11 line-clamp-2">
                        "{request.message}"
                      </p>
                    )}
                  </div>
                ))
              ) : (
                <div className="px-5 py-8 text-center">
                  <p className="text-sm text-gray-500">No pending requests</p>
                </div>
              )}
            </div>
          </div>

          {/* Alerts Link */}
          <Link
            to="/seller/notifications"
            className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-colors"
          >
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-orange-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">View All Alerts</p>
              <p className="text-xs text-gray-500">Hot lead notifications</p>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, subtext, trend, color, link, highlight }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  const content = (
    <div className={`p-4 ${highlight ? 'ring-2 ring-orange-300 ring-offset-2' : ''}`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend !== undefined && trend !== 0 && (
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
            trend > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-600">{label}</p>
      {subtext && <p className="text-xs text-gray-400 mt-0.5">{subtext}</p>}
    </div>
  );

  const baseClass = "bg-white rounded-xl border border-gray-200 hover:shadow-md transition-shadow";

  if (link) {
    return <Link to={link} className={baseClass}>{content}</Link>;
  }

  return <div className={baseClass}>{content}</div>;
}

function InterestBadge({ level, score }) {
  const config = {
    hot: { bg: 'bg-orange-100', text: 'text-orange-700', icon: '🔥' },
    warm: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: '' },
    cold: { bg: 'bg-gray-100', text: 'text-gray-600', icon: '' },
  };

  const { bg, text, icon } = config[level] || config.cold;

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${bg} ${text}`}>
      {score}% {icon}
    </span>
  );
}

function formatTimeAgo(timestamp) {
  if (!timestamp) return 'Unknown';

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}
